import { select, put, call } from 'redux-saga/effects';
import { AnyAction } from 'redux';
import { RootState } from 'MyTypes';
import { AxiosResponse } from 'axios';
import { getUnixTime, differenceInDays, fromUnixTime } from 'date-fns';
import Survicate from '@survicate/survicate-web-package/survicate_widget';
import { apiClient } from '@/utils';
import { getXlanguageCode } from '@/utils/i18n';
import bmApi from '@/services/bmApi';
import { whenSurvicateReady, trackSurvicateEvent } from '@/utils/survicate';

// DB-backed feature flag (IsPublic, deployment-wide) gating the Survicate
// usage-metrics enrichment and event-triggered surveys (not identification,
// which runs for every company). Resolved per request via GET /feature/{companyId};
// toggled by SuperAdmin. See ai-assistant for the same pattern.
export const SURVICATE_FEATURE = 'survicate';

// localStorage key holding the Unix timestamp (seconds) of when the company was
// submitted for approval. Written in SetupAccount, read here on every login so
// `days_since_submitted_for_approval` can be recomputed as time passes.
export const SURVICATE_SUBMITTED_FOR_APPROVAL_KEY = 'survicate_submitted_for_approval_at';

// localStorage key holding the last support case ("{Id}:{Updated}") we already
// fired the survey event for, so we only invoke it once per newly-closed case.
const SURVICATE_SUPPORT_CASE_KEY = 'survicate_last_closed_support_case';

// SupportCaseStatus ids that count as "closed" (ClosedRejected, ClosedSolved).
const CLOSED_SUPPORT_CASE_STATUS_IDS = [4, 5];

// Only fire the support survey for cases closed within this many days, so old
// closed cases don't trigger it on a fresh browser.
const SUPPORT_CASE_RECENCY_DAYS = 14;

export function* isSurvicateEnabled(companyId: string): Generator<any, boolean, any> {
    // @ts-ignore
    const flagsRequest = yield put(
        bmApi.endpoints.getCompanyFeatureFlags.initiate({ CompanyId: companyId })
    );
    yield flagsRequest;
    const flagsResult: { data?: { Name: string; IsEnabled: boolean }[] } = yield select(
        // @ts-ignore
        bmApi.endpoints.getCompanyFeatureFlags.select({ CompanyId: companyId })
    );
    return (flagsResult?.data ?? []).some((f) => f.Name === SURVICATE_FEATURE && f.IsEnabled);
}

// Fires a Survicate event for the current company when the `survicate` flag is
// on. Never throws: callers run it after a committed side effect (e.g. a
// successful DELETE), where a thrown error would surface as a false failure.
export function* invokeSurvicateEvent(eventName: string) {
    try {
        if (!process.env.REACT_APP_SURVICATE_WORKSPACE_KEY) {
            return;
        }
        // Same id source as runSurvicate and the Full.tsx flag subscription, so the
        // flags query hits the existing RTK cache entry instead of a new one.
        const companyId: string | undefined = yield select(
            (state: RootState) => state.company.data?.Id
        );
        if (!companyId) {
            return;
        }
        const enabled: boolean = yield call(isSurvicateEnabled, String(companyId));
        if (enabled) {
            whenSurvicateReady(() => {
                trackSurvicateEvent(eventName);
                Survicate.invokeEvent(eventName);
            });
        }
    } catch (error) {
        console.error(error);
    }
}

export function* runSurvicate({ payload }: AnyAction) {
    try {
        if (!process.env.REACT_APP_SURVICATE_WORKSPACE_KEY) {
            return;
        }

        const company: RootState['company'] = yield select((state) => state.company);

        const language = getXlanguageCode();

        const created = new Date(company.data.Created);
        const daysSinceSignup = differenceInDays(new Date(), created);

        // Recompute days-since-submitted from the persisted timestamp (if present)
        // so the "2 months after approval" survey audience grows over time.
        const submittedRaw = window.localStorage.getItem(SURVICATE_SUBMITTED_FOR_APPROVAL_KEY);
        const submittedAt = submittedRaw ? Number(submittedRaw) : null;
        const daysSinceSubmitted =
            submittedAt && !Number.isNaN(submittedAt)
                ? differenceInDays(new Date(), fromUnixTime(submittedAt))
                : null;

        // Identify the company on every session, before any survey can be answered,
        // so responses are attributable. Not gated by the `survicate` feature flag:
        // Survicate is initialized for every company with the workspace key, so a
        // non-flagged company would otherwise answer surveys anonymously.
        // Values are computed eagerly; only the SDK call waits for readiness.
        const baseTraits = {
            // Identifies the visitor so attribute-based ("logged-in users")
            // audiences match. Company-scoped, as the surveys are per company.
            user_id: company.data.Id,
            ...(language && { language }),
            created_at: getUnixTime(created),
            days_since_signup: daysSinceSignup,
            companyId: company.data.Id,
            // @ts-ignore
            categoryId: company.data.CategoryId,
            isFreeAccount: company.data.IsFreeAccount,
            ...(daysSinceSubmitted !== null && {
                days_since_submitted_for_approval: daysSinceSubmitted,
            }),
        };
        whenSurvicateReady(() => Survicate.setVisitorTraits(baseTraits));

        // Feature-flag-gated extras: backend usage metrics and event-triggered
        // surveys stay behind the `survicate` flag; only identification above is
        // unconditional.
        const survicateEnabled: boolean = yield call(isSurvicateEnabled, company.data.Id);
        if (!survicateEnabled) {
            return;
        }

        // Backend-computed usage counts (paid bookings per provider, code lock
        // synced bookings) that the admin UI cannot derive on its own. Non-fatal:
        // a failure here must not block the base traits above.
        try {
            const metrics: AxiosResponse = yield apiClient.get('/survicate/metrics', {
                params: { CompanyId: company.data.Id },
            });
            if (metrics?.data) {
                const metricsTraits = {
                    stripe_paid_bookings_count: metrics.data.StripePaidBookingsCount,
                    qvickly_paid_bookings_count: metrics.data.QvicklyPaidBookingsCount,
                    codelock_synced_bookings_count: metrics.data.CodelockSyncedBookingsCount,
                };
                // Queued after the base traits, so they still land in that order.
                whenSurvicateReady(() => Survicate.setVisitorTraits(metricsTraits));
            }
        } catch (metricsError) {
            console.error(metricsError);
        }

        // Support cases are closed server-side, so detect a recently-closed case
        // on login and fire the "support case closed" survey once per case.
        try {
            const casesRes: AxiosResponse = yield apiClient.get('/support/cases', {
                params: { OrderByDesc: 'Updated', Take: 20 },
            });
            const cases: any[] = casesRes?.data?.Results ?? [];
            const latestClosed = cases.find((c) =>
                CLOSED_SUPPORT_CASE_STATUS_IDS.includes(c.CaseStatusId)
            );

            if (latestClosed?.Updated) {
                const closedRecently =
                    differenceInDays(new Date(), new Date(latestClosed.Updated)) <=
                    SUPPORT_CASE_RECENCY_DAYS;
                const closedKey = `${latestClosed.Id}:${latestClosed.Updated}`;
                const lastHandled = window.localStorage.getItem(SURVICATE_SUPPORT_CASE_KEY);

                if (closedRecently && closedKey !== lastHandled) {
                    // The key means "invoked after ready", not "delivered": the SDK
                    // still drops the event while another survey is open. The
                    // re-check stops two runs queued before ready from both firing.
                    whenSurvicateReady(() => {
                        if (window.localStorage.getItem(SURVICATE_SUPPORT_CASE_KEY) === closedKey) {
                            return;
                        }
                        trackSurvicateEvent('support_case_closed');
                        Survicate.invokeEvent('support_case_closed');
                        window.localStorage.setItem(SURVICATE_SUPPORT_CASE_KEY, closedKey);
                    });
                }
            }
        } catch (supportError) {
            console.error(supportError);
        }
    } catch (error) {
        console.error(error);
    }
}
